import { useLayoutEffect, useState, type RefObject } from 'react';

export interface DocumentPreviewDockFrame {
  top: number;
  bottom: number;
}

const TOOLBAR_SELECTOR = '.cpsToolbarContainer';
const PAGER_SELECTOR = '.customGridPager';

function readOffset(element: Element | null, containerRect: DOMRect, edge: 'top' | 'bottom'): number {
  if (!(element instanceof HTMLElement)) {
    return 0;
  }

  const rect = element.getBoundingClientRect();
  if (edge === 'top') {
    return Math.max(0, rect.bottom - containerRect.top);
  }
  return Math.max(0, containerRect.bottom - rect.top);
}

export function useDocumentPreviewDock(
  gridRef: RefObject<HTMLDivElement | null>,
  isOpen: boolean
): DocumentPreviewDockFrame | undefined {
  const [frame, setFrame] = useState<DocumentPreviewDockFrame | undefined>(undefined);

  useLayoutEffect(() => {
    const container = gridRef.current;
    if (!isOpen || !container) {
      setFrame(undefined);
      return;
    }

    const measure = (): void => {
      const containerRect = container.getBoundingClientRect();
      setFrame({
        top: readOffset(container.querySelector(TOOLBAR_SELECTOR), containerRect, 'top'),
        bottom: readOffset(container.querySelector(PAGER_SELECTOR), containerRect, 'bottom')
      });
    };

    measure();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(measure);
    observer.observe(container);
    const toolbar = container.querySelector(TOOLBAR_SELECTOR);
    const pager = container.querySelector(PAGER_SELECTOR);
    if (toolbar instanceof HTMLElement) {
      observer.observe(toolbar);
    }
    if (pager instanceof HTMLElement) {
      observer.observe(pager);
    }

    return () => {
      observer.disconnect();
    };
  }, [gridRef, isOpen]);

  return frame;
}
